import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Flex,
  HStack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import {
  Address,
  CheckboxField,
  colors,
  dobField,
  Field,
  FieldTypes,
  getDateString,
  getMostRecentAssessment,
  mergeData,
  phoneField,
  UserInvite,
} from '@hb/shared'

import { useCallback, useContext, useMemo, useState } from 'react'

import { AppContext, PopUpMessageContext } from '@hb/react'
import { deleteField } from 'firebase/firestore'
import { manuallySetEmailVerified } from '../../../../backend/auth/utils'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useUpdateDoc } from '../../../../hooks/backend/useUpdateDoc'
import { ActionLog } from '../../../ActionLog'
import { CopyId } from '../../../CopyId'
import { Editable, EditableRow } from '../../../forms'
import { UserBadge } from '../../UserBadge'

const emailField: Field = {
  type: FieldTypes.EMAIL,
  placeholder: 'Email',
  optional: true,
}

const InviteSummary = ({ invite }: { invite: UserInvite }) => {
  const invitedOnText = useMemo(
    () => `INVITED ${invite.createdOn ? ` ${getDateString(invite.createdOn, 'short')}` : ''}`,
    [invite],
  )
  return (
    <HStack>
      <Text fontSize="sm" fontWeight="bold" opacity={0.75} color="color">
        {invitedOnText}
      </Text>
      {invite.createdBy ? <UserBadge userId={invite.createdBy} /> : null}
    </HStack>
  )
}

// const JoinedSummary = ({ user }: { user: User | null }) => {
//   const joinedOnText = useMemo(
//     () => `Joined${user?.createdOn ? ` on ${getDateString(user.createdOn)}` : ''}`,
//     [user],
//   )
//   return (
//     <Text fontSize="sm" fontFamily="Open Sans">
//       {joinedOnText}
//     </Text>
//   )
// }

const checkboxVoicemailOkField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  labels: [{ text: 'Is it ok to leave a voicemail?' }],
  placeholder: 'Voicemail OK?',
}
const checkboxOptIntoTextField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  labels: [
    {
      text: 'Is it ok to send you text messages?',
    },
  ],
  placeholder: 'Text messages OK?',
}

export const EmailVerificationCheckbox = ({ inV2Profile }: { inV2Profile?: boolean }) => {
  const { emailVerification, patientRef, user } = useProfile()
  const { refetch } = emailVerification ?? {}
  const { isInvite } = user ?? {}
  const [manuallyVerifying, setManuallyVerifying] = useState(false)
  const { processResponse } = useContext(PopUpMessageContext)
  const handleManuallyVerifyEmail = useCallback(async () => {
    if (!patientRef) return processResponse({ error: 'No patient ref' })
    if (isInvite)
      return processResponse({ error: 'Cannot manually verify email for an invite profile' })
    setManuallyVerifying(true)
    try {
      await manuallySetEmailVerified(patientRef.id)
      refetch()
      setManuallyVerifying(false)

      return { success: 'Email set as verified' }
    } catch (err: any) {
      setManuallyVerifying(false)
      return processResponse({
        error: err?.message || 'Error manually verifying email',
      })
    }
  }, [patientRef, processResponse, refetch, isInvite])
  return (
    <Flex align="center" gap={2}>
      {emailVerification?.loading ? (
        <CircularProgress isIndeterminate size={4} />
      ) : (
        <Checkbox size="sm" colorScheme="green" isChecked={emailVerification?.verified}>
          {emailVerification?.verified ? 'Verified' : 'Not verified'}
        </Checkbox>
      )}
      {emailVerification?.loading || emailVerification?.verified ? null : (
        <Tooltip
          bg="orange.600"
          placement="left"
          hasArrow
          label={isInvite ? 'Cannot verify email for an invite profile' : ''}>
          <Button
            size="xs"
            isLoading={manuallyVerifying}
            onClick={handleManuallyVerifyEmail}
            opacity={isInvite ? 0.5 : 1}
            bg={colors.green.hex}
            color="white">
            {inV2Profile ? 'Manually Verify' : 'Manually Verify Email'}
          </Button>
        </Tooltip>
      )}
    </Flex>
  )
}

const PatientEmailVerificationView = ({
  noCheckbox = false,
  inV2Profile = false,
}: {
  noCheckbox?: boolean
  inV2Profile?: boolean
}) => {
  const { user } = useProfile()

  const { emailVerified, isInvite } = user ?? {}

  return (
    <HStack align="center">
      {emailVerified ? (
        <ActionLog
          group="admin"
          style={{ width: 'auto', padding: 0 }}
          action="Verified"
          on={emailVerified.on}
          by={emailVerified.by}
        />
      ) : null}
      {noCheckbox ? null : <EmailVerificationCheckbox inV2Profile={inV2Profile} />}
    </HStack>
  )
}

export const PatientContact = ({ inV2Profile = false }: { inV2Profile?: boolean }) => {
  const {
    user,
    patientRef,
    selectedAssessment: { populated: selectedAssessment },
    assessmentId,
    assessments,
  } = useProfile()
  const { email, isInvite, phone, dob, voicemailOk, textOk } = user ?? {}

  const { appName } = useContext(AppContext)
  const displayedAssessment = useMemo(
    () => selectedAssessment ?? getMostRecentAssessment(assessments ?? {}),
    [selectedAssessment, assessments],
  )
  const { signOnData, signOnCorrections } = displayedAssessment ?? {}

  const usedAddress = useMemo(() => {
    if (assessmentId) {
      return signOnData?.homeAddressSameAsPatientAddress ||
        signOnCorrections?.homeAddressSameAsPatientAddress
        ? selectedAssessment?.plans?.primary?.address
        : mergeData(signOnData?.homeAddress ?? {}, signOnCorrections?.homeAddress ?? {})
    }
    return user?.insurancePlans?.primary?.address
  }, [signOnCorrections, signOnData, selectedAssessment, assessmentId, user])
  const patientAddress = useMemo(() => {
    if (!usedAddress || Object.keys(usedAddress).length === 0) return 'None'
    const { streetAddress, streetAddress2, city, state, zip } = usedAddress as Address
    let res = streetAddress ?? ''
    if (streetAddress2) res += ` ${streetAddress2}\n`
    else res += '\n'
    res += `${city || 'No city'}, ${state || 'No state'} `
    res += zip || 'No ZIP'
    return res
  }, [usedAddress])
  const update = useUpdateDoc('patient')

  return (
    <Flex position="relative" w="100%" flexFlow="column">
      {isInvite ? (
        <InviteSummary invite={user as UserInvite} />
      ) : // <JoinedSummary user={user} />
      null}
      <VStack pb={2} pl={1} spacing={0} w="100%" align="flex-start">
        <Flex align="center" w="100%">
          {inV2Profile ? null : (
            <Text flex={1} fontWeight={600} color="gray.600" lineHeight={1} fontSize="sm">
              Email:
            </Text>
          )}
          {inV2Profile ? null : <EmailVerificationCheckbox />}
        </Flex>
        <Flex align="center" w="100%">
          <Flex minW="0" gap={1} flex={1}>
            {isInvite ? (
              <Editable
                field={emailField}
                size="md"
                value={email}
                onSubmit={async v => update(patientRef, '', { email: v || deleteField() })}
              />
            ) : (
              <Text pb={1} ml={1}>
                {email}
              </Text>
            )}
          </Flex>
        </Flex>
        {inV2Profile ? (
          <PatientEmailVerificationView inV2Profile />
        ) : isInvite ? null : (
          <PatientEmailVerificationView noCheckbox />
        )}
      </VStack>
      <VStack spacing={0} w="100%" align="flex-start">
        <Box w="220px">
          <EditableRow
            label="Phone"
            stackProps={{ p: 0 }}
            field={phoneField}
            value={phone}
            onSubmit={async v => update(patientRef, '', { phone: v })}
          />
        </Box>
        <Editable
          field={checkboxVoicemailOkField}
          value={voicemailOk}
          onSubmit={async v => update(patientRef, '', { voicemailOk: v })}
        />
        <Editable
          field={checkboxOptIntoTextField}
          value={textOk}
          onSubmit={async v => update(patientRef, '', { textOk: v })}
        />
      </VStack>
      <HStack pl={1} spacing={2} w="100%" align="center">
        <Text lineHeight={1} fontWeight={600} color="gray.600" fontSize="sm">
          Date of Birth:
        </Text>
        <Editable
          style={{ background: 'transparent', padding: 0 }}
          dataCellProps={{ style: { padding: '0.2rem 0' }, fontSize: 'sm' }}
          field={dobField}
          value={dob}
          onSubmit={async v => update(patientRef, '', { dob: v })}
        />
      </HStack>
      {appName === 'app' ? (
        <VStack pl={1} spacing={0} w="100%" align="flex-start">
          <Text fontWeight={600} color="gray.600" fontSize="sm">
            Address {assessmentId ? '(from sign-on info)' : '(from primary insurance plan)'}:
          </Text>
          <Text ml={1} fontSize="15px" whiteSpace="pre-wrap">
            {patientAddress}
          </Text>
        </VStack>
      ) : null}
      <CopyId item={user} id={patientRef?.id} />
    </Flex>
  )
}
